import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Link,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { ReadingColumn, SiteShell } from '../components/site/SiteChrome';
import { POLICIES, type PolicySlug } from '../content/policies';
import { SITE } from '../content/site';

/**
 * A policy paragraph with its `[text](/path)` links: a path opens in the
 * app, `mailto:` the mail app.
 */
export function withLinks(text: string): ReactNode[] {
  return text.split(/(\[[^\]]+\]\([^)\s]+\))/g).map((piece, index) => {
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(piece);
    if (!link) return <Fragment key={index}>{piece}</Fragment>;
    const [, label, target] = link;
    return target!.startsWith('/') ? (
      <Link
        key={index}
        as={RouterLink}
        to={target!}
        color="accent"
        fontWeight={600}
      >
        {label}
      </Link>
    ) : (
      <Link key={index} href={target} color="accent" fontWeight={600}>
        {label}
      </Link>
    );
  });
}

// Privacy, terms, refunds and contact (M42, D108).
export function PolicyPage({ slug }: { slug: PolicySlug }) {
  const policy = POLICIES[slug];
  return (
    <SiteShell>
      <ReadingColumn>
        <VStack align="stretch" spacing={8}>
          <VStack align="stretch" spacing={3}>
            <Heading
              as="h1"
              fontSize={{ base: '36px', md: '48px' }}
              lineHeight={1.05}
            >
              {policy.title}
            </Heading>
            <Text fontSize="18px" lineHeight="27px" color="ink.body">
              {policy.intro}
            </Text>
            {slug !== 'contact' && (
              <Text fontSize="14px" color="muted">
                Last updated {SITE.policiesUpdated}
              </Text>
            )}
          </VStack>
          {policy.sections.map((section) => (
            <Box as="section" key={section.heading}>
              <Heading as="h2" fontSize="22px" pb={3}>
                {section.heading}
              </Heading>
              <VStack align="stretch" spacing={3}>
                {section.body.map((block, index) =>
                  typeof block === 'string' ? (
                    <Text key={index} lineHeight="26px" color="ink.body">
                      {withLinks(block)}
                    </Text>
                  ) : (
                    <UnorderedList
                      key={index}
                      spacing={2}
                      pl={1}
                      color="ink.body"
                    >
                      {block.list.map((item) => (
                        <ListItem key={item} lineHeight="26px">
                          {withLinks(item)}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  )
                )}
              </VStack>
            </Box>
          ))}
        </VStack>
      </ReadingColumn>
    </SiteShell>
  );
}
